import {
  LazyQueryHookOptions,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import { DocumentNode } from 'graphql';
import { merge } from 'lodash';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useLocale } from 'state/intl';
import { useNetworkContext } from 'state/network';

const useSanityGqlEndpoint = () => {
  const networkCtx = useNetworkContext();
  return networkCtx.sanityEndpoints.graphql;
};

export const useSanityQuery = <
  TData = any,
  TVariables extends OperationVariables = Record<string, any>,
>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
) => {
  const uri = useSanityGqlEndpoint();
  const { language, region } = useLocale();

  const queryOptions = merge(options, { context: { uri } });

  const queryResult = useQuery<TData, TVariables>(query, queryOptions);

  const { refetch } = queryResult;

  useEffectOnUpdates(() => {
    refetch();
  }, [language, region]);

  return queryResult;
};

export const useLazySanityQuery = <
  TData = any,
  TVariables extends OperationVariables = Record<string, any>,
>(
  query: DocumentNode,
  options: LazyQueryHookOptions<TData, TVariables> = {}
) => {
  const uri = useSanityGqlEndpoint();

  const queryOptions = merge(options, { context: { uri } });

  return useLazyQuery<TData, TVariables>(query, queryOptions);
};

// for apollo codegen compatibility
export * from '@apollo/client';
export { useSanityQuery as useQuery, useLazySanityQuery as useLazyQuery };
